import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, Loader2 } from "lucide-react";
import PageHead from "@/components/PageHead";
import { api } from "@/lib/api";
import { toast } from "sonner";

const COLUMNS = ["Applied", "Screening", "Interview", "Offer"];

const TONE = {
  Applied: "border-t-electric",
  Screening: "border-t-aqua",
  Interview: "border-t-violet",
  Offer: "border-t-lime",
};

export default function RecruiterPipeline() {
  const [board, setBoard] = useState({
    Applied: [],
    Screening: [],
    Interview: [],
    Offer: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetchPipeline = async () => {
      try {
        const { data } = await api.get("/recruiter/pipeline");
        if (active && data?.pipeline) {
          setBoard(data.pipeline);
        }
      } catch (_) {
        if (active) {
          setBoard({ Applied: [], Screening: [], Interview: [], Offer: [] });
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchPipeline();
    return () => {
      active = false;
    };
  }, []);

  const move = (col, card) => {
    const idx = COLUMNS.indexOf(col);
    if (idx >= COLUMNS.length - 1) return;
    const next = COLUMNS[idx + 1];
    setBoard((b) => {
      const copy = { ...b };
      copy[col] = (copy[col] || []).filter((c) => c.id !== card.id);
      copy[next] = [...(copy[next] || []), card];
      return copy;
    });
    toast.success(`${card.name} → ${next}`);
  };

  return (
    <div>
      <PageHead
        label="Candidate Pipeline"
        title="Move candidates to hire"
        sub="Advance candidates through each recruitment stage. Tap the arrow to progress."
      />

      {loading ? (
        <div className="flex min-h-[300px] items-center justify-center rounded-3xl border border-dashed border-border">
          <Loader2 className="h-6 w-6 animate-spin text-violet" />
          <span className="ml-2 text-xs text-muted-foreground">Loading recruitment pipeline…</span>
        </div>
      ) : (
        <div className="-mx-5 overflow-x-auto px-5 pb-4 sm:mx-0 sm:px-0">
          <div className="flex min-w-max gap-4">
            {COLUMNS.map((col) => {
              const columnCards = board[col] || [];
              return (
                <div
                  key={col}
                  className={`w-64 rounded-2xl border border-t-4 border-border bg-secondary/40 p-3.5 ${TONE[col]}`}
                >
                  <p className="mb-3 flex items-center justify-between text-sm font-bold">
                    <span>{col}</span>
                    <span className="rounded-full bg-card px-2 py-0.5 text-xs font-semibold text-muted-foreground border border-border">
                      {columnCards.length}
                    </span>
                  </p>

                  <div className="space-y-2.5">
                    <AnimatePresence mode="popLayout">
                      {columnCards.map((c) => (
                        <motion.div
                          key={c.id}
                          layout
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          className="group rounded-xl border border-border bg-card p-3.5 shadow-xs transition-shadow hover:shadow-card"
                          data-testid={`pipeline-card-${c.id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-foreground truncate">{c.name}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {c.role} · {c.match || 88}% fit
                              </p>
                            </div>
                            {COLUMNS.indexOf(col) < COLUMNS.length - 1 && (
                              <button
                                onClick={() => move(col, c)}
                                className="rounded-md p-1 text-muted-foreground hover:bg-secondary hover:text-violet"
                                title={`Advance to ${COLUMNS[COLUMNS.indexOf(col) + 1]}`}
                              >
                                <ChevronRight className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>

                    {columnCards.length === 0 && (
                      <p className="py-8 text-center text-xs text-muted-foreground border border-dashed border-border/60 rounded-xl">
                        No candidates
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
